import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';
import DateTime from 'pkg/datetime';
import { crash } from 'pkg/errors/errors';

export async function deleteAttachment(
	organizationId: number,
	attachmentId: number,
	postId: number
) {
	return sdk.destroy(
		endpoints.GroupPosts.RemoveAttachment(organizationId, postId, attachmentId)
	);
}

interface Payload {
	newAttachments: models.attachment.Attachment[];
	content: string;
	title: string;
	status: string;
	authorGroupId?: number;
	isSticky: boolean;
	stickyUntil: number;
	commentsDisabled: boolean;
	notify: boolean;
	publishedAt: number;
	visibleToLegalGuardians: boolean;
	visibleToUsers: boolean;
	visibleToOrganization: boolean;
	isPublic: boolean;
	includeGroupIds: [];
}

export const updateGroupPost = async (
	organizationId: number,
	postId: number,
	payload: Payload
) => {
	if (!payload.isSticky) {
		payload.stickyUntil = 0;
	}

	Object.values(payload.newAttachments || {}).forEach(async (a) => {
		await sdk.post(
			endpoints.GroupPosts.AddAttachment(organizationId, postId, a.id)
		);
	});

	const request = await sdk.patch(
		endpoints.GroupPosts.Update(organizationId, postId),
		{},
		payload
	);
	const result = await request.json();

	if (request.ok) {
		flashActions.show(
			{
				title: t(`Post saved`),
			},
			request.status,
			'post_add'
		);
	}

	return [request, result];
};

export const createGroupPost = async (
	organizationId: number,
	groupId: number,
	payload: Payload
) => {
	if (!payload.isSticky) {
		payload.stickyUntil = 0;
	}

	const [request, response] = await create<
		Payload,
		models.groupPost.GroupPost
	>(endpoints.GroupPosts.Create(organizationId, groupId), payload);

	if (request.ok) {
		response.attachments = [];

		Object.values(payload.newAttachments || {}).forEach(async (a) => {
			response.attachments.push(a);
			await sdk.post(
				endpoints.GroupPosts.AddAttachment(organizationId, response.id, a.id)
			);
		});

		let flashTitle = '';
		let flashMessage = '';
		switch (response.status) {
			case 'draft':
				flashTitle = t('Draft created');
				flashMessage = t('Find it in drafts');
				break;
			case 'published':
				flashTitle = t('Post created');
				flashMessage = t(`It's now available on the wall`);
				break;
			case 'scheduled':
				flashTitle = t('Post scheduled');
				flashMessage = t('It will auto publish on {publish_date}', {
					publish_date: new DateTime(
						new Date(response.publishedAt * 1000)
					).toLocaleDateString(),
				});
				break;
		}

		flashActions.show(
			{
				title: flashTitle,
				message: flashMessage,
			},
			request.status,
			'post_add'
		);
	}

	return [request, response];
};

export const markAsSeen = (
	organizationId: number,
	userId: number,
	groupId: number
) => {
	sdk.post(endpoints.GroupPosts.AddView(organizationId, groupId));
};

export const deleteGroupPost = async (organizationId: number, id: number) => {
	const request = await sdk.destroy(
		endpoints.GroupPosts.Delete(organizationId, id),
		{}
	);
	if (request.ok) {
		flashActions.show(
			{
				title: t(`Post removed`),
			},
			200,
			'post_add'
		);
		return true;
	}

	return false;
};

export const toggleLike = async (
	organizationId: number,
	isLiked: boolean,
	post: models.groupPost.GroupPost
) => {
	if (isLiked) {
		sdk.destroy(endpoints.GroupPosts.RemoveLike(organizationId, post.id), {
			id: post.id,
		});
	} else {
		create<null, models.groupPostComment.GroupPostComment>(
			endpoints.GroupPosts.AddLike(organizationId, post.id),
			null
		);
	}
};

export const addComment = async (
	organizationId: number,
	userId: number,
	postId: number,
	commentText: string
) => {
	const [request, response, error] = await create<
		{ userId: number; comment: string },
		models.groupPostComment.GroupPostComment
	>(endpoints.GroupPosts.AddComment(organizationId, postId), {
		userId,
		comment: commentText,
	});

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: error.error,
		});

		return null;
	}

	return response;
};

export const updateComment = async (
	organizationId: number,
	id: number,
	comment: string
) => {
	const req = await sdk.patch(
		endpoints.GroupPosts.UpdateComment(organizationId, id),
		{},
		{ comment }
	);

	if (!req.ok) {
		return;
	}

	const resp = await req.json();
	return resp;
};

export const deleteComment = async (
	organizationId: number,
	commentId: number
) => {
	const req = await sdk.destroy(
		endpoints.GroupPosts.DeleteComment(organizationId, commentId)
	);
	if (!req.ok) {
		return false;
	}

	return true;
};
